import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useSnackbar } from '../contexts/SnackbarProvider';
import GlobalError from '../components/GlobalError';
import axiosInstance from '../utils/axiosInstance';
import ClientInfoForm from '../components/ClientInfoForm';
import TableView from '../components/TableView';
import ContactModal from '../components/ContactModal';
import ClientTabs from '../components/ClientTabs';
import { Typography, Button, Stack, Chip, Paper, Alert } from '@mui/material';
import { Sync, Close, Edit, Apartment, Person, PersonAdd, Delete } from '@mui/icons-material';

const ClientDetailPage = ({ hasLimit }) => {
    const { showSnackbar } = useSnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [clientData, setClientData] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContact, setModalContact] = useState(null);
    const openEditModal = (contact = null) => {
        setModalContact(contact);
        setIsModalOpen(true);
    };

    const closeEditModal = () => {
        setModalContact(null);
        setIsModalOpen(false);
    };
    const statusMap = {
        pending: { label: '申請中', color: 'warning' },
        active: { label: '有効', color: 'success' },
    };
    const { register, handleSubmit, control, watch, formState: { errors }, setValue, reset } = useForm({
        defaultValues: {
            industry_id: clientData?.industry_id || '',
            payment_method_id: clientData?.payment_method_id || '',
        }
    });
    //const items = watch('items') || [];

    const fetchClient = useCallback(async () => {
        try {
            const res = await axiosInstance.get(`/client/${id}`);
            setClientData(res.data);
        } catch (err) {
            if (process.env.NODE_ENV === 'development') { console.error(err); }
            setClientData(null);
            setError(err?.response?.data?.message || '顧客情報の取得に失敗しました');
        }
    }, [id]);
    const handleInvalidate = async () => {
        const isConfirmed = window.confirm(
            '本当に削除しますか？削除後は復元できません。'
        );
        if (!isConfirmed) return;

        try {
            const res = await axiosInstance.put(`/client/${id}/invalidate`);
            navigate(`/clients`, {
                state: {
                    snackbar: { message: '顧客情報を削除しました', severity: 'success' }
                }
            });
        } catch (err) {
            console.error(err);
            showSnackbar(err?.response?.data?.message || '削除に失敗しました', 'error');
        }
    };
    useEffect(() => {
        fetchClient();
    }, [fetchClient]);

    useEffect(() => {
        const snackbarState = location.state?.snackbar;
        if (snackbarState) {
            showSnackbar(snackbarState.message, snackbarState.severity);
            navigate(location.pathname, { replace: true });
        }
    }, [location.state, navigate, showSnackbar]);

    // 更新ボタンの処理
    const onSubmit = async (data) => {
        try {
            const res = await axiosInstance.put(`/client/${id}`, {
                ...data
            });
            showSnackbar(res?.data?.message || '取引先情報が更新されました', 'success');
            fetchClient();
            setIsEditMode(false);
        } catch (err) {
            if (process.env.NODE_ENV === 'development') { console.error(err); }
            showSnackbar(err?.response?.data?.message || '更新に失敗しました', 'error');
        }
    };
    // 取引先を無効にするボタンのクリック

    const handleApprove = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.put(`/client/${id}/status`, { status: 'active' });
            showSnackbar(res.data.message, 'success');
            fetchClient?.();
        } catch (err) {
            showSnackbar(err.response?.data?.message || '承認に失敗しました', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (error) return <GlobalError errorMessage={error} />;
    if (!clientData) return null;

    return (
        <>
            <Typography variant="h1" component="h1">
                <Chip label='取引先情報' sx={{ mb: 2, fontWeight: 'normal' }} /><br />
                {clientData.client_name}</Typography>
            <ClientTabs clientId={id} />
            <Paper variant='section'>
                {/* 会社情報入力 */}
                <Stack direction="row" alignItems="center" justifyContent='space-between' spacing={1} mb={1}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <Apartment color="primary" />
                        <Typography component='h2' variant='h4'>会社情報</Typography>
                        <Chip
                            label={statusMap[clientData?.status]?.label || '作成中'}
                            color={statusMap[clientData?.status]?.color || 'warning'}
                        />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            type="submit"
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<Delete />}
                            onClick={() => handleInvalidate()}
                        >
                            削除する
                        </Button>
                    </Stack>
                </Stack>
                {clientData.status === 'pending' && (
                    <Alert severity="error" sx={{ my: 1 }}>
                        取引を許可する場合は承認ボタンを押してください
                    </Alert>
                )}
                <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
                    <ClientInfoForm
                        clientData={clientData}
                        setClientData={setClientData}
                        isEditMode={isEditMode}
                        setIsEditMode={setIsEditMode}
                        register={register}
                        errors={errors}
                        setValue={setValue}
                        control={control}
                        watch={watch}
                        refetch={fetchClient}
                    />
                    <div className="button-row">
                        <Button
                            variant='outlined'
                            startIcon={isEditMode ? <Close /> : <Edit />}
                            onClick={() => {
                                if (isEditMode) { fetchClient(); }
                                setIsEditMode(prev => !prev);
                            }}
                        >
                            {isEditMode ? '編集を破棄する' : '設定を編集する'}
                        </Button>
                        {/* 更新ボタン */}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!isEditMode}
                            startIcon={<Sync />}
                        >
                            内容を更新する
                        </Button>
                        {clientData.status !== 'active' && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleApprove}
                                disabled={loading}
                            >
                                承認
                            </Button>
                        )}
                    </div>
                </form>
            </Paper>
            {/* 担当者情報入力 */}
            <Paper variant='section'>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Person color="primary" />
                        <Typography component='h2' variant='h4'>担当者情報</Typography>
                    </Stack>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PersonAdd />}
                        onClick={() => openEditModal(null)}
                    >
                        担当者を追加
                    </Button>
                </Stack>
                {clientData.items?.length === 0 && (
                    <Alert severity="error" sx={{ my: 1 }}>
                        担当者情報を登録してください
                    </Alert>
                )}
                {hasLimit('ec') && clientData.items?.some(item => item.status === 'pending') && (
                    <Alert severity="warning" sx={{ my: 1 }}>
                        有効化されていない担当者がいます。有効化されていない場合、ECサイトへのアクセスができません。
                    </Alert>
                )}
                {hasLimit('ec') && (
                    <Alert severity="info" sx={{ my: 1 }}>
                        詳細ボタンをクリックし、ECサイトへのアクセスを許可する担当者へパスワードを設定するメールを送信しましょう。
                    </Alert>
                )}
                <TableView
                    dataset={clientData.items || []}
                    keyColumn="client_contact_id"
                    columns={[
                        {
                            label: '担当者名', accessor: 'client_contact_name',
                            render: (contact) => {
                                return <Typography>{contact.client_contact_name || ''}</Typography>;
                            }, width: 200
                        },
                        {
                            label: 'メール', accessor: 'client_contact_email',
                            render: (contact) => {
                                return <Typography>{contact.client_contact_email || ''}</Typography>;
                            }, width: 300
                        },
                        { label: '電話', accessor: 'client_contact_phone', width: 200 },
                        {
                            label: 'ステータス',
                            accessor: 'status',
                            render: (contact) => {
                                const map = statusMap[contact.status] || { label: '作成中', color: 'default' };
                                return <Chip label={map.label} color={map.color} size="small" />;
                            }, width: 100
                        },
                        {
                            label: '',
                            render: (contact) => (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => openEditModal(contact)}
                                >
                                    詳細
                                </Button>
                            ), width: 100
                        }
                    ]}
                />
            </Paper>
            <ContactModal
                hasLimit={hasLimit}
                open={isModalOpen}
                onClose={closeEditModal}
                contact={modalContact}
                clientId={id}
                refetch={fetchClient}
            />
        </>
    );
};

export default ClientDetailPage;
